// Auth — simple allowlist + shared passphrase gate.
//
// No backend, no tokens. We validate the email against the editable config
// (src/config/access.ts) and the passphrase against the shared phrase, then
// remember the session in localStorage so the gate doesn't reappear on every
// launch. This is access control, not identity — see the note in access.ts.

import { ALLOWLIST, ALLOW_DOMAINS, PASSPHRASE } from "../config/access";

const SESSION_KEY = "bvndle-cc:session";

export interface Session {
  email: string;
  at: string; // ISO — when they signed in
}

function norm(email: string): string {
  return email.trim().toLowerCase();
}

function domainOf(email: string): string {
  const at = email.lastIndexOf("@");
  return at === -1 ? "" : email.slice(at + 1);
}

/** Is this email permitted (allowlist entry OR allowed domain)? */
export function isEmailAllowed(email: string): boolean {
  const e = norm(email);
  if (!e.includes("@")) return false;
  if (ALLOWLIST.some((a) => norm(a) === e)) return true;
  return ALLOW_DOMAINS.some((d) => domainOf(e) === d.trim().toLowerCase());
}

export type LoginError = "bad-email" | "not-allowed" | "bad-passphrase";

/** Attempt a login. Returns the Session on success, or a typed error. */
export function attemptLogin(email: string, passphrase: string): Session | LoginError {
  const e = norm(email);
  if (!e.includes("@") || e.length < 5) return "bad-email";
  if (!isEmailAllowed(e)) return "not-allowed";
  if (passphrase !== PASSPHRASE) return "bad-passphrase";
  const session: Session = { email: e, at: new Date().toISOString() };
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* private mode — session just won't persist */
  }
  return session;
}

/** Read a persisted session, re-validating against the current allowlist. */
export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    // Re-check: if the person was removed from the allowlist since last login,
    // their stored session stops working.
    if (!s?.email || !isEmailAllowed(s.email)) return null;
    return s;
  } catch {
    return null;
  }
}

export function logout(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* no-op */
  }
}
